import { useCallback } from 'react';
import { STARTER_TEMPLATES, type StarterTemplate } from '@/lib/templates';
import { useEditorStore } from '../store';

interface OnboardingModalProps {
  open: boolean;
  onDismiss: () => void;
}

export function OnboardingModal({ open, onDismiss }: OnboardingModalProps) {
  const loadDiagram = useEditorStore((s) => s.loadDiagram);
  const setMode = useEditorStore((s) => s.setMode);

  const pick = useCallback(
    (tpl: StarterTemplate) => {
      setMode(tpl.mode);
      loadDiagram(tpl.diagram);
      onDismiss();
    },
    [loadDiagram, setMode, onDismiss],
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-clyra-bg/80 backdrop-blur-sm px-4">
      <div className="w-full max-w-3xl rounded-clyra-lg border border-line-default bg-clyra-raised/95 shadow-clyra-lg">
        <div className="flex items-center justify-between border-b border-line-subtle px-6 py-4">
          <div>
            <h2 className="text-base font-semibold text-ink-primary">Start with a template</h2>
            <p className="mt-0.5 text-micro text-ink-secondary">
              Pick a starting point, or dismiss and describe your system.
            </p>
          </div>
          <button
            onClick={onDismiss}
            className="rounded-clyra px-2 py-1 text-tiny text-ink-muted hover:text-ink-primary hover:bg-clyra-overlay transition-colors"
          >
            Skip
          </button>
        </div>

        <div className="grid gap-3 p-6 sm:grid-cols-2">
          {STARTER_TEMPLATES.map((tpl) => (
            <button
              key={tpl.id}
              type="button"
              onClick={() => pick(tpl)}
              className="group rounded-clyra-lg border border-line-default bg-clyra-bg/40 p-4 text-left transition-all hover:border-brand-500/40 hover:bg-brand-500/5"
            >
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-ink-primary">{tpl.title}</h3>
                <span className="rounded-full bg-clyra-overlay/60 px-2 py-0.5 text-micro text-ink-faint uppercase tracking-wider">
                  {tpl.mode.split('_')[0]}
                </span>
              </div>
              <p className="mt-1.5 text-tiny text-ink-secondary leading-relaxed">{tpl.summary}</p>
              <p className="mt-3 text-micro text-ink-faint">
                {tpl.diagram.nodes.length} nodes · {tpl.diagram.edges.length} edges
              </p>
            </button>
          ))}
        </div>

        <div className="border-t border-line-subtle px-6 py-3 text-center text-micro text-ink-faint">
          You can always start over from the editor.
        </div>
      </div>
    </div>
  );
}
